/**
 * MD-11 FE-CHAT — Citation metadata parser (display-only, FE-only).
 *
 * Spec: docs/superpowers/specs/2026-05-11-chat-leftbar-source-upgrade-design.md §3.7
 * DECISIONS: s2026-05-11-CHAT-LEFTBAR-SOURCE-UPGRADE
 *
 * Parses `doc_id`, `provision_path` từ backend `CitationRef` để render rich
 * SourceCard metadata (doc_type, doc_number, doc_name, year, issuer, eff status).
 *
 * Contract: pure functions, KHÔNG mutate input. Unknown / unparseable trả về
 * `UNKNOWN` type + empty field, KHÔNG throw, KHÔNG silent fail — log warn qua
 * console.warn để builder thấy trong DevTools (PROCESS-BASELINE rule 5).
 *
 * Backend `CitationRef.doc_id` examples (verified production 2026-05-11):
 *   - `vbpl_45_2019_qh14`           → Luật 45/2019/QH14 (Luật Lao động 2019)
 *   - `vbpl_91_2015_qh13`           → Bộ luật Dân sự 91/2015/QH13
 *   - `vbpl_13_2023_nd_cp`          → Nghị định 13/2023/ND-CP
 *   - `vbpl_78_2021_tt_btc`         → Thông tư 78/2021/TT-BTC
 *   - `vbpl_03_2021_nq_hdtp`        → Nghị quyết 03/2021/NQ-HDTP
 *   - `vbpl_02_2016_al`             → Án lệ 02/2016/AL
 *   - `vbpl_hp_2013`                → Hiến pháp 2013
 */

import type { CitationRefShape } from '../../../src/workers/refusal/types';

// ---- Types ----

export type DocType =
  | 'LUAT'
  | 'BO_LUAT'
  | 'ND'
  | 'TT'
  | 'NQ'
  | 'AL'
  | 'HP'
  | 'UNKNOWN';

export interface ParsedDocMeta {
  doc_type: DocType;
  doc_number?: string;     // "45/2019/QH14"
  doc_name?: string;       // best-effort display name "Luật 45/2019/QH14"
  year?: string;           // "2019"
  issuer?: string;         // "Quốc hội" / "Chính phủ" / "Bộ Tài chính" ...
}

export interface ParsedProvision {
  chuong?: string;
  dieu?: string;
  khoan?: string;
  diem?: string;
}

export type EffStatus = CitationRefShape['eff_status_at_render'];

export interface EffStatusLabel {
  label: string;
  color: string;
  tone: 'success' | 'warning' | 'danger' | 'muted';
}

// ---- Issuer suffix map ----

interface IssuerEntry {
  re: RegExp;
  vn: string;
  en: string;
}

const ISSUER_MAP: IssuerEntry[] = [
  { re: /^QH\d*$/i, vn: 'Quốc hội', en: 'National Assembly' },
  { re: /^UBTVQH\d*$/i, vn: 'Ủy ban Thường vụ Quốc hội', en: 'Standing Committee of the NA' },
  { re: /^ND-CP$/i, vn: 'Chính phủ', en: 'Government' },
  { re: /^NQ-CP$/i, vn: 'Chính phủ', en: 'Government' },
  { re: /^QD-TTG$/i, vn: 'Thủ tướng Chính phủ', en: 'Prime Minister' },
  { re: /^TT-BTC$/i, vn: 'Bộ Tài chính', en: 'Ministry of Finance' },
  { re: /^TT-BLDTBXH$/i, vn: 'Bộ Lao động - Thương binh và Xã hội', en: 'MOLISA' },
  { re: /^TT-BCA$/i, vn: 'Bộ Công an', en: 'Ministry of Public Security' },
  { re: /^TT-BTNMT$/i, vn: 'Bộ Tài nguyên và Môi trường', en: 'Ministry of Natural Resources' },
  { re: /^TT-BYT$/i, vn: 'Bộ Y tế', en: 'Ministry of Health' },
  { re: /^TT-BGDDT$/i, vn: 'Bộ Giáo dục và Đào tạo', en: 'Ministry of Education' },
  { re: /^TT-BTP$/i, vn: 'Bộ Tư pháp', en: 'Ministry of Justice' },
  { re: /^TT-BXD$/i, vn: 'Bộ Xây dựng', en: 'Ministry of Construction' },
  { re: /^TT-BCT$/i, vn: 'Bộ Công Thương', en: 'Ministry of Industry and Trade' },
  { re: /^TT-NHNN$/i, vn: 'Ngân hàng Nhà nước', en: 'State Bank of Vietnam' },
  { re: /^NQ-HDTP$/i, vn: 'Hội đồng Thẩm phán TANDTC', en: 'SPC Judges Council' },
  { re: /^AL$/i, vn: 'Tòa án nhân dân tối cao', en: 'Supreme People’s Court' },
];

export function issuerFromSuffix(suffix: string, lang: 'vi' | 'en' = 'vi'): string {
  if (!suffix) return '';
  for (const entry of ISSUER_MAP) {
    if (entry.re.test(suffix)) return lang === 'vi' ? entry.vn : entry.en;
  }
  return '';
}

// ---- Doc-type detection ----

function detectDocType(suffix: string, docIdLower: string): DocType {
  if (/^QH\d*$/i.test(suffix) || /^UBTVQH/i.test(suffix)) {
    // distinguish BO_LUAT vs LUAT — heuristic on suffix alone not possible;
    // default to LUAT (display) — caller may override if backend richer.
    return 'LUAT';
  }
  if (/^ND-CP$/i.test(suffix) || /^NQ-CP$/i.test(suffix)) return 'ND';
  if (/^TT-/i.test(suffix)) return 'TT';
  if (/^NQ-HDTP$/i.test(suffix)) return 'NQ';
  if (/^AL$/i.test(suffix)) return 'AL';
  if (/^hp(_|$)/i.test(docIdLower)) return 'HP';
  return 'UNKNOWN';
}

// ---- doc_id parser ----

/**
 * Parse backend `doc_id` (snake_case ASCII) → display metadata.
 *
 * Pattern (common): `vbpl_<number>_<year>_<suffix>` where suffix is the issuer
 * code (qh14, nd_cp, tt_btc, nq_hdtp, al, ...). Special: `vbpl_hp_<year>`.
 *
 * Defensive: unknown shape → returns `{ doc_type: 'UNKNOWN' }` + console.warn
 * (NOT silent fail per PROCESS-BASELINE rule 5).
 */
export function parseDocMeta(doc_id: string, lang: 'vi' | 'en' = 'vi'): ParsedDocMeta {
  if (!doc_id || typeof doc_id !== 'string') {
    return { doc_type: 'UNKNOWN' };
  }
  const lower = doc_id.toLowerCase();

  // Special Hien phap: `vbpl_hp_2013`
  const hpMatch = lower.match(/^vbpl_hp_(\d{4})$/);
  if (hpMatch) {
    return {
      doc_type: 'HP',
      year: hpMatch[1],
      doc_name: lang === 'vi' ? `Hiến pháp ${hpMatch[1]}` : `Constitution ${hpMatch[1]}`,
      issuer: lang === 'vi' ? 'Quốc hội' : 'National Assembly',
    };
  }

  // Generic: vbpl_<num>_<year>_<suffix...>
  const m = lower.match(/^vbpl_(\d+)_(\d{4})_(.+)$/);
  if (!m) {
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[citation-meta] parseDocMeta: unrecognized doc_id shape', doc_id);
    }
    return { doc_type: 'UNKNOWN' };
  }
  const num = m[1];
  const year = m[2];
  const suffixRaw = m[3];                            // "qh14" / "nd_cp" / "tt_btc"
  const suffix = suffixRaw.toUpperCase().replace(/_/g, '-');
  const doc_number = `${num}/${year}/${suffix}`;
  const doc_type = detectDocType(suffix, lower);
  const issuer = issuerFromSuffix(suffix, lang);
  return {
    doc_type,
    doc_number,
    year,
    issuer,
    doc_name: undefined,                             // caller fills from server if available
  };
}

// ---- provision_path parser ----

/**
 * Parse `provision_path` string into structured segments.
 *
 * Backend format examples:
 *   - "Điều 8"
 *   - "Điều 8 / Khoản 2"
 *   - "Chương II / Điều 8 / Khoản 2 / Điểm a"
 *
 * Defensive: unknown segment ignored; empty input → empty struct.
 */
export function parseProvision(provision_path: string | undefined | null): ParsedProvision {
  if (!provision_path || typeof provision_path !== 'string') return {};
  const out: ParsedProvision = {};
  const parts = provision_path.split('/').map((s) => s.trim()).filter(Boolean);
  for (const p of parts) {
    const lower = p.toLowerCase();
    if (lower.startsWith('chương') || lower.startsWith('chuong')) {
      out.chuong = p.replace(/^(chương|chuong)\s+/i, '').trim();
    } else if (lower.startsWith('điều') || lower.startsWith('dieu')) {
      out.dieu = p.replace(/^(điều|dieu)\s+/i, '').trim();
    } else if (lower.startsWith('khoản') || lower.startsWith('khoan')) {
      out.khoan = p.replace(/^(khoản|khoan)\s+/i, '').trim();
    } else if (lower.startsWith('điểm') || lower.startsWith('diem')) {
      out.diem = p.replace(/^(điểm|diem)\s+/i, '').trim();
    }
  }
  return out;
}

// ---- Snippet derive ----

/**
 * Derive a display snippet from the full assistant content + citation span.
 * Returns the substring covering `[span_start - ctx, span_end + ctx]`,
 * wrapped in `"..."` if truncated on either side.
 *
 * Defensive: negative/out-of-range indices clamped; empty content → empty.
 */
export function deriveSnippet(
  content: string,
  span_start: number,
  span_end: number,
  ctx_chars: number = 100
): string {
  if (!content || typeof content !== 'string') return '';
  const len = content.length;
  if (len === 0) return '';
  const s = Math.max(0, Math.min(span_start | 0, len));
  const e = Math.max(s, Math.min(span_end | 0, len));
  const lo = Math.max(0, s - ctx_chars);
  const hi = Math.min(len, e + ctx_chars);
  const core = content.slice(lo, hi).trim();
  const prefix = lo > 0 ? '...' : '';
  const suffix = hi < len ? '...' : '';
  return `${prefix}${core}${suffix}`;
}

// ---- Eff status label ----

const EFF_STATUS_MAP: Record<EffStatus, { vn: string; en: string; color: string; tone: EffStatusLabel['tone'] }> = {
  CHL: { vn: 'Đang hiệu lực', en: 'In force', color: '#10b981', tone: 'success' },
  CCHL: { vn: 'Chưa có hiệu lực', en: 'Not yet in force', color: '#F5B800', tone: 'warning' },
  HHL: { vn: 'Hết hiệu lực', en: 'Expired', color: '#6b7280', tone: 'muted' },
  TNHL: { vn: 'Tạm ngưng hiệu lực', en: 'Suspended', color: '#c0392b', tone: 'danger' },
  KCPH: { vn: 'Không công bố', en: 'Not published', color: '#6b7280', tone: 'muted' },
  'KXĐ': { vn: 'Không xác định', en: 'Undetermined', color: '#6b7280', tone: 'muted' },
};

export function effStatusLabel(status: EffStatus | undefined, lang: 'vi' | 'en' = 'vi'): EffStatusLabel {
  const entry = status && EFF_STATUS_MAP[status];
  if (!entry) {
    return {
      label: lang === 'vi' ? 'Không xác định' : 'Undetermined',
      color: '#6b7280',
      tone: 'muted',
    };
  }
  return {
    label: lang === 'vi' ? entry.vn : entry.en,
    color: entry.color,
    tone: entry.tone,
  };
}

// ---- Doc-type display name ----

const DOC_TYPE_LABEL_VN: Record<DocType, string> = {
  LUAT: 'Luật',
  BO_LUAT: 'Bộ luật',
  ND: 'Nghị định',
  TT: 'Thông tư',
  NQ: 'Nghị quyết',
  AL: 'Án lệ',
  HP: 'Hiến pháp',
  UNKNOWN: 'Văn bản',
};

const DOC_TYPE_LABEL_EN: Record<DocType, string> = {
  LUAT: 'Law',
  BO_LUAT: 'Code',
  ND: 'Decree',
  TT: 'Circular',
  NQ: 'Resolution',
  AL: 'Case law',
  HP: 'Constitution',
  UNKNOWN: 'Document',
};

export function docTypeLabel(type: DocType, lang: 'vi' | 'en' = 'vi'): string {
  return lang === 'vi' ? DOC_TYPE_LABEL_VN[type] : DOC_TYPE_LABEL_EN[type];
}

/**
 * Best-effort display name: prefer server-supplied `doc_name`, else
 * compose from doc_type + doc_number + year.
 */
export function displayDocName(meta: ParsedDocMeta, lang: 'vi' | 'en' = 'vi'): string {
  if (meta.doc_name) return meta.doc_name;
  const type = docTypeLabel(meta.doc_type, lang);
  if (meta.doc_type === 'HP' && meta.year) return `${type} ${meta.year}`;
  if (meta.doc_number) return `${type} ${meta.doc_number}`;
  return type;
}
